import type { JSX } from "react";
import { Suspense } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { FormSection } from "@impero-com/common/components/formSection/formSection";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { Button, FormLabel, PageSpinner } from "@impero-com/ui";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";

import { OrgApplicationBar } from "components/applicationBar/orgApplicationBar";
import {
    accessRightSchema,
    RHFAccessRights,
} from "components/form/rhfFields/rhfAccessRights";
import { StickyFooter } from "components/stickyFooter/footer";
import { FormFooterWrapper } from "components/stickyFooter/FormFooterWrapper";
import { WebRoute } from "routes";
import {
    formatToAdminEditViewAccessRights,
    formatToUserIdAndRight,
} from "utils/accessRights";
import { SavedReportAccessRightsInstance } from "utils/accessRights/models";

import {
    useSavedReportAccessRightsQuery,
    useUpdateSavedReportAccessRightsMutation,
} from "../queries";

const accessRightsFormSchema = z.object({
    userIdAndRights: accessRightSchema(SavedReportAccessRightsInstance),
});
type AccessRightsFormSchema = z.infer<typeof accessRightsFormSchema>;

export function ReportAccessRightsPage(): JSX.Element {
    const { t } = useTranslation();
    const { savedReportId } = useParams();

    if (!savedReportId) {
        return <LoggedInHTTPError code={404} />;
    }

    return (
        <OrgApplicationBar
            title={{
                title: t("Report_EditAccessRights_Title"),
                breadCrumbs: [
                    { label: t("Report_BreadCrumb"), url: WebRoute.REPORTS },
                ],
            }}
        >
            <Suspense fallback={<PageSpinner />}>
                <View key={savedReportId} savedReportId={savedReportId} />
            </Suspense>
        </OrgApplicationBar>
    );
}

interface ViewProps {
    savedReportId: string;
}

function View({ savedReportId }: ViewProps): JSX.Element {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { data } = useSavedReportAccessRightsQuery(savedReportId);
    const { mutateAsync, isPending } =
        useUpdateSavedReportAccessRightsMutation();

    const { control, formState, handleSubmit } =
        useForm<AccessRightsFormSchema>({
            resolver: zodResolver(accessRightsFormSchema),
            mode: "onBlur",
            defaultValues: {
                userIdAndRights: formatToUserIdAndRight(data.accessRights),
            },
        });

    const handleSave = handleSubmit(async ({ userIdAndRights }) => {
        const accessRights = formatToAdminEditViewAccessRights(userIdAndRights);
        await mutateAsync({
            id: savedReportId,
            data: {
                Id: savedReportId,
                UserIdsWithAdminAccess: accessRights.admin,
                UserIdsWithEditAccess: accessRights.edit,
                UserIdsWithViewAccess: accessRights.view,
            },
        });
        navigate(WebRoute.REPORTS);
    });

    return (
        <FormFooterWrapper>
            <FormSection
                id="saved-report-access-rights"
                titleLabel={t("Report_EditAccessRights_Title")}
            >
                <FormLabel>{t("Report_Edit_Assign_Users")}</FormLabel>
                <RHFAccessRights
                    control={control}
                    users={data.users}
                    name="userIdAndRights"
                    resourceAccessRights={SavedReportAccessRightsInstance}
                />
            </FormSection>
            <StickyFooter>
                <Button
                    size="large"
                    variant="primary"
                    disabled={!formState.isDirty}
                    loading={isPending}
                    onClick={() => void handleSave()}
                >
                    {t("Report_EditAccessRights_Save")}
                </Button>
                <Button
                    size="large"
                    variant="default"
                    onClick={() => navigate(WebRoute.REPORTS)}
                >
                    {t("Cancel")}
                </Button>
            </StickyFooter>
        </FormFooterWrapper>
    );
}
